import lodashGet from "lodash/get";
import { renderPillarTableHeading } from "shared/components/pillar-table/helpers/renderPillarTableHeading";

/**
 * How a nested table is written out on export.
 */
export type PillarTableNestedTableExportLayout = {
  /**
   * `rows` (the default) writes one row per nested item on a sheet of its own.
   * `columns` spreads the nested items across the main sheet instead, one block
   * of columns per group.
   */
  as: "rows" | "columns";
  /**
   * Groups the nested items on this path, so that each distinct value gets its
   * own block of columns, e.g. `profile.profileId` for one block per reviewer.
   * Leave it out to group by position: the 1st item of every row, then the 2nd…
   */
  groupByPath?: string;
  /**
   * Handlebars template that labels a group; it prefixes each of the group's
   * column headings. Grouped on a path, it renders against the group's first
   * item plus `groupKey` and `groupNumber`, e.g.
   * `{{profile.societyUser.firstName}} {{profile.societyUser.lastName}}`.
   * Grouped by position it only gets `groupNumber`, because the items in one
   * position differ from row to row, e.g. `Reviewer {{groupNumber}}`.
   */
  groupHeading?: string;
  /** With `as: "columns"`, also write the usual one-row-per-item sheet. */
  includeRowsSheet?: boolean;
};

type PivotPillarTableNestedRowsProps<NestedRow> = {
  /** The nested items of each parent row, in parent row order */
  nestedRowsByParent: NestedRow[][];
  /** See `PillarTableNestedTableExportLayout.groupByPath` */
  groupByPath?: string;
  /** See `PillarTableNestedTableExportLayout.groupHeading` */
  groupHeading?: string;
  /** The heading of each nested column, in column order */
  columnHeadings: string[];
  /** The cell values of one nested item, one per nested column */
  renderNestedRow: (nestedRow: NestedRow) => unknown[];
};

type PivotPillarTableNestedRowsResult = {
  /** The headings of every pivoted column */
  headings: string[];
  /** The pivoted cells for each parent row, aligned with `nestedRowsByParent` */
  rows: unknown[][];
};

type PillarTableNestedGroup<NestedRow> = {
  /** The first item seen in this group; its values label the group */
  firstRow: NestedRow;
  /**
   * The most items any one parent row has in this group. A reviewer who voted
   * twice on one submission needs a second set of columns for it.
   */
  slotCount: number;
};

const defaultGroupHeading = ({
  groupByPath,
}: {
  groupByPath?: string;
}): string => (groupByPath ? "{{groupKey}}" : "#{{groupNumber}}");

/**
 * Turns the nested items of each parent row into extra columns on that row.
 *
 * Groups come out in the order they are first seen. Every parent row gets the
 * same columns, blank where it has nothing in a group, so the result lines up
 * under a single header row.
 */
export const pivotPillarTableNestedRows = <NestedRow>({
  nestedRowsByParent,
  groupByPath,
  groupHeading,
  columnHeadings,
  renderNestedRow,
}: PivotPillarTableNestedRowsProps<NestedRow>): PivotPillarTableNestedRowsResult => {
  const groupedByParent = nestedRowsByParent.map((nestedRows) => {
    const groupedRows = new Map<string, NestedRow[]>();
    nestedRows.forEach((nestedRow, nestedRowIndex) => {
      const groupKey = groupByPath
        ? String(lodashGet(nestedRow, groupByPath))
        : String(nestedRowIndex);
      groupedRows.set(groupKey, [
        ...(groupedRows.get(groupKey) ?? []),
        nestedRow,
      ]);
    });
    return groupedRows;
  });

  const groups = new Map<string, PillarTableNestedGroup<NestedRow>>();
  groupedByParent.forEach((groupedRows) => {
    groupedRows.forEach((rowsInGroup, groupKey) => {
      const group = groups.get(groupKey);
      if (!group) {
        groups.set(groupKey, {
          firstRow: rowsInGroup[0],
          slotCount: rowsInGroup.length,
        });
        return;
      }
      group.slotCount = Math.max(group.slotCount, rowsInGroup.length);
    });
  });

  const headingTemplate = groupHeading ?? defaultGroupHeading({ groupByPath });
  const headings = [...groups.entries()].flatMap(
    ([groupKey, { firstRow, slotCount }], groupIndex) => {
      const groupNumber = groupIndex + 1;
      const firstRowData =
        groupByPath && typeof firstRow === "object" && firstRow !== null
          ? firstRow
          : {};
      const groupLabel = renderPillarTableHeading({
        heading: headingTemplate,
        templateData: { ...firstRowData, groupKey, groupNumber },
      });

      return Array.from({ length: slotCount }, (_, slotIndex) =>
        columnHeadings.map((columnHeading) => {
          const heading = `${groupLabel} ${columnHeading}`.trim();
          return slotIndex === 0 ? heading : `${heading} (${slotIndex + 1})`;
        }),
      ).flat();
    },
  );

  const emptyCells = columnHeadings.map(() => "");
  const rows = groupedByParent.map((groupedRows) =>
    [...groups.entries()].flatMap(([groupKey, { slotCount }]) => {
      const rowsInGroup = groupedRows.get(groupKey) ?? [];
      return Array.from({ length: slotCount }, (_, slotIndex) =>
        rowsInGroup[slotIndex] === undefined
          ? emptyCells
          : renderNestedRow(rowsInGroup[slotIndex]),
      ).flat();
    }),
  );

  return { headings, rows };
};
